import { Box, Typography, makeStyles } from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import * as React from "react";

import { CustomButton } from "@/components/Button";
import { PLANNER_MOTION } from "@/components/planner_onboarding/plannerMotion";
import { PlannerReveal, usePlannerActionReady, usePlannerReducedMotion } from "@/components/planner_onboarding/PlannerReveal";
import { plannerIntroductionActionStyles } from "@/layouts/PlannerIntroductionLayout";
import theme from "@/theme";

import CertifiedPlannerPyramid from "@assets/image/planner_onboarding/character_certified_pyramid.png";

const ASSURANCES = [
  "各不動産会社の中でも、お客様評価が高いエキスパート",
  "売り上げ、従業員数が一定規模以上の信用できる会社に所属している",
  "プロパリーが定める、一定以上の売買経験がある",
];

const useStyles = makeStyles(() => ({
  heading: { fontSize: 20, fontWeight: 700, lineHeight: 1.3, textAlign: "center" },
  accent: { color: theme.palette.semantic["action/primary"] },
  illustration: { display: "flex", justifyContent: "center", padding: "24px 0" },
  illustrationCrop: { width: 276, maxWidth: "100%", aspectRatio: "276 / 246", position: "relative", overflow: "hidden" },
  pyramid: {
    // Figma Flow-2 の画像レイヤー（1829:1815）と同じ切り抜き位置。
    position: "absolute", width: "100%", height: "112.56%", maxWidth: "none",
    left: "-0.43%", top: "-8.7%", objectFit: "fill",
  },
  card: { borderRadius: 16, overflow: "hidden", backgroundColor: theme.palette.semantic["surface/card"], boxShadow: "0 2px 8px rgba(22,32,46,0.06)" },
  cardTitle: { padding: "16px 20px", backgroundColor: theme.palette.semantic["surface/trust"], color: theme.palette.common.white, textAlign: "center", fontSize: 16, fontWeight: 700 },
  rows: { padding: "8px 20px" },
  row: { display: "flex", gap: 12, alignItems: "center", padding: "20px 0", "& + &": { borderTop: `1px solid ${theme.palette.semantic["border/default"]}` } },
  check: { fontSize: 24, color: theme.palette.semantic["surface/trust"], flexShrink: 0 },
  text: { fontSize: 14, lineHeight: 1.5 },
  action: plannerIntroductionActionStyles,
}));

export const CertifiedPlannerIntroduction: React.FC<{ onNext: () => void; animate?: boolean }> = ({ onNext, animate = true }) => {
  const classes = useStyles();
  const systemReduced = usePlannerReducedMotion();
  const reduced = systemReduced || !animate;
  const ready = usePlannerActionReady(PLANNER_MOTION.certifiedNext, reduced);
  return (
    <>
      <PlannerReveal animate={!reduced}>
        <Typography component="h1" className={classes.heading}>
          安心して相談できる<br /><span className={classes.accent}>認定プランナー制度</span>
        </Typography>
      </PlannerReveal>
      <PlannerReveal delay={PLANNER_MOTION.greeting} animate={!reduced} className={classes.illustration}>
        <Box className={classes.illustrationCrop}>
          <img src={CertifiedPlannerPyramid} alt="認定プランナーを表すピラミッドと、頂点のプーロくん" className={classes.pyramid} />
        </Box>
      </PlannerReveal>
      <PlannerReveal delay={PLANNER_MOTION.definition} animate={!reduced} className={classes.card}>
        <Typography component="h2" className={classes.cardTitle}>認定プランナーの条件</Typography>
        <Box className={classes.rows}>
          {ASSURANCES.map((text, index) => (
            <PlannerReveal
              key={text}
              delay={PLANNER_MOTION.certifiedCondition + index * PLANNER_MOTION.certifiedConditionStagger}
              duration={PLANNER_MOTION.certifiedConditionDuration}
              animate={!reduced}
              className={classes.row}
            >
              <CheckCircleIcon className={classes.check} />
              <Typography className={classes.text}>{text}</Typography>
            </PlannerReveal>
          ))}
        </Box>
      </PlannerReveal>
      <PlannerReveal delay={PLANNER_MOTION.certifiedNext} animate={!reduced} className={classes.action}>
        <CustomButton tone="primary" buttonSize="L" fullWidth disabled={!ready} onClick={onNext}>プランナーを選ぶ</CustomButton>
      </PlannerReveal>
    </>
  );
};
